// Modal reCAPTCHA challenge shown when the server answers a redraw (/svg) or download
// (/download) request with 403 { recaptcha_required: true, recaptcha_site_key }. That
// only happens for an account an admin has flagged as a level-2 suspected bot: every such
// request must prove humanness before the dieline is served (see
// ApplicationController#challenge_suspected_bot_level_2). Returns a Promise that resolves
// with the reCAPTCHA token once the checkbox is solved, and rejects if the visitor
// dismisses the dialog or the widget fails to load. The caller re-issues the original
// request with the token in g-recaptcha-response (see DrawingSvg / DownloadConfirmation).

// render=explicit defers widget creation to our grecaptcha.render call; onload fires once
// the api.js has finished loading. The domains here are the same ones the server-rendered
// not_robot / contact_us reCAPTCHA already loads, so no new hosts are introduced.
const RECAPTCHA_SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit&onload=__botChallengeRecaptchaLoaded';

// Load the reCAPTCHA script at most once per page, reused across challenges.
let recaptchaLoadPromise = null;

const ensureRecaptchaLoaded = () => {
  if (window.grecaptcha && window.grecaptcha.render) {
    return Promise.resolve(window.grecaptcha);
  }
  if (recaptchaLoadPromise) {
    return recaptchaLoadPromise;
  }

  recaptchaLoadPromise = new Promise((resolve, reject) => {
    window.__botChallengeRecaptchaLoaded = () => { resolve(window.grecaptcha); };
    const script = document.createElement('script');
    script.src = RECAPTCHA_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      // Let a later challenge retry the load instead of caching the failure forever.
      recaptchaLoadPromise = null;
      reject(new Error('recaptcha_script_failed'));
    };
    document.head.appendChild(script);
  });

  return recaptchaLoadPromise;
};

// Reads a user-facing string from the global i18n-js `t` (window.t), falling back to
// English if `t` is absent (e.g. before the runtime loads) or the key is missing.
const label = (key, fallback) => {
  if (typeof t === 'function') {
    const value = t(key);
    if (value && value.indexOf('missing') === -1) { return value; }
  }
  return fallback;
};

const showBotChallenge = (siteKey) => {
  return new Promise((resolve, reject) => {
    if (!siteKey) {
      reject(new Error('recaptcha_site_key_missing'));
      return;
    }

    ensureRecaptchaLoaded().then((grecaptcha) => {
      const overlay = document.createElement('div');
      overlay.setAttribute('style', 'position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.55);');

      const card = document.createElement('div');
      card.setAttribute('style', 'background:#fff;border-radius:12px;padding:26px 28px;max-width:360px;width:calc(100% - 40px);box-shadow:0 10px 40px rgba(0,0,0,.25);text-align:center;font-family:inherit;');

      const heading = document.createElement('h3');
      heading.textContent = label('bot_challenge.heading', 'Please verify you are human');
      heading.setAttribute('style', 'margin:0 0 8px;font-size:18px;color:#222;');

      const description = document.createElement('p');
      description.textContent = label('bot_challenge.description', 'To continue, confirm you are not a robot.');
      description.setAttribute('style', 'margin:0 0 18px;font-size:14px;color:#666;line-height:1.45;');

      const widget = document.createElement('div');
      widget.setAttribute('style', 'display:flex;justify-content:center;');

      const cancel = document.createElement('button');
      cancel.type = 'button';
      cancel.textContent = label('bot_challenge.cancel', 'Cancel');
      cancel.setAttribute('style', 'margin-top:18px;appearance:none;border:0;background:transparent;color:#999;font-size:13px;cursor:pointer;');

      card.appendChild(heading);
      card.appendChild(description);
      card.appendChild(widget);
      card.appendChild(cancel);
      overlay.appendChild(card);
      document.body.appendChild(overlay);

      let settled = false;
      const close = () => {
        if (overlay.parentNode) { overlay.parentNode.removeChild(overlay); }
      };

      cancel.addEventListener('click', () => {
        if (settled) { return; }
        settled = true;
        close();
        reject(new Error('bot_challenge_dismissed'));
      });

      grecaptcha.render(widget, {
        sitekey: siteKey,
        callback: (token) => {
          if (settled) { return; }
          settled = true;
          close();
          resolve(token);
        },
        'error-callback': () => {
          if (settled) { return; }
          settled = true;
          close();
          reject(new Error('recaptcha_error'));
        }
      });
    }).catch(reject);
  });
};

export default showBotChallenge;
